'use client';

import { Box, Button, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import SpaceY from '../../../components/SpaceY';
import useStatsigExperimentOverride, {
  EXPERIMENT_VARIANT,
  EXPERIMENT_VARIANT_OPTIONS,
} from '../../../utils/hook/useStatsigExperimentOverride';

/** 실험 이름 규칙 — 번호만 입력받아 이 접두를 붙인다 */
const EXPERIMENT_NAME_PREFIX = 'eid';

/** 목록에 새로 추가할 때 기본으로 잡아 두는 실험안 */
const DEFAULT_VARIANT = EXPERIMENT_VARIANT.TEST_A;

/** 실험 번호는 숫자만 받는다 */
const NUMBER_ONLY_PATTERN = /^\d*$/;

/** 실험명 → 실험안 번호 */
type OverrideDraft = Record<string, number>;

/**
 * Statsig 실험 강제 배정 — 실험 번호를 추가하고 실험안을 고른 뒤 변경 버튼으로 적용한다
 *
 * 콘솔 Overrides에 stableID를 등록하고 반영을 기다리는 과정을 건너뛰기 위한 로컬 개발 도구다.
 * 편집은 초안에만 쌓이고 변경 버튼을 눌러야 저장된다 — 고르는 즉시 저장하면 실험 훅이
 * 곧바로 다시 평가돼 화면이 중간 상태로 바뀐다.
 * 강제 배정된 실험은 exposure가 기록되지 않는다.
 */
const StatsigExperimentOverrideSection = () => {
  const { overrides, replaceOverrides } = useStatsigExperimentOverride();

  const [experimentNumber, setExperimentNumber] = useState('');
  /** 편집 중인 목록. 아직 손대지 않았으면 null이고 저장값을 그대로 보여준다 */
  const [draft, setDraft] = useState<OverrideDraft | null>(null);

  const currentOverrides = draft ?? overrides;
  const overrideEntries = Object.entries(currentOverrides);

  const trimmedNumber = experimentNumber.trim();
  const experimentName = `${EXPERIMENT_NAME_PREFIX}${trimmedNumber}`;
  const isAlreadyAdded = experimentName in currentOverrides;
  const canAdd = !!trimmedNumber && !isAlreadyAdded;

  const hasChanges =
    draft !== null && JSON.stringify(draft) !== JSON.stringify(overrides);

  const handleNumberChange = (value: string) => {
    if (!NUMBER_ONLY_PATTERN.test(value)) return;
    setExperimentNumber(value);
  };

  const handleAdd = () => {
    setDraft({ ...currentOverrides, [experimentName]: DEFAULT_VARIANT });
    setExperimentNumber('');
  };

  const handleSelectVariant = (targetName: string, variant: number) =>
    setDraft({ ...currentOverrides, [targetName]: variant });

  const handleRemove = (targetName: string) => {
    const { [targetName]: _, ...rest } = currentOverrides;
    setDraft(rest);
  };

  /** 저장하고 진입 시점부터 다시 그린다 */
  const handleApply = () => {
    if (!draft) return;

    replaceOverrides(draft);
    window.location.reload();
  };

  return (
    <Box className="p-16px">
      <Typography className="mb-8px text-16px font-bold">
        Statsig 실험 강제 배정
      </Typography>
      <Typography className="mb-4px text-12px text-gray-600">
        번호를 추가하고 실험안을 고른 뒤 변경 버튼을 누릅니다. (localhost 전용)
      </Typography>
      <Typography className="mb-8px text-12px font-bold text-red-500">
        ⚠️ 화면만 바꾸는 도구입니다. 실험이 콘솔에 제대로 등록·시작됐는지는
        Statsig 콘솔에서 직접 확인하세요.
      </Typography>

      <Box className="flex items-start gap-8px">
        <TextField
          label="실험 번호"
          placeholder="453"
          value={experimentNumber}
          onChange={(e) => handleNumberChange(e.target.value)}
          error={isAlreadyAdded}
          helperText={isAlreadyAdded ? '이미 목록에 있습니다.' : ' '}
          size="small"
          fullWidth
          inputMode="numeric"
        />
        <Button
          variant="contained"
          size="small"
          disabled={!canAdd}
          onClick={handleAdd}
          className="mt-4px shrink-0"
        >
          추가
        </Button>
      </Box>

      {overrideEntries.length === 0 ? (
        <Typography className="text-12px text-gray-600">
          강제 배정된 실험이 없습니다. 모든 실험이 정상 배정으로 평가됩니다.
        </Typography>
      ) : (
        <ul className="flex flex-col gap-16px rounded-[8px] border border-gray-200 p-16px">
          {overrideEntries.map(([overriddenName, overriddenVariant]) => (
            <li key={overriddenName} className="flex flex-col gap-4px">
              <Box className="flex items-center justify-between gap-8px">
                <p className="text-14px font-bold">
                  🧪 {overriddenName.toUpperCase()}
                </p>
                <Button
                  variant="text"
                  size="small"
                  color="error"
                  onClick={() => handleRemove(overriddenName)}
                >
                  해제
                </Button>
              </Box>
              {/* 드롭다운은 메뉴가 Drawer 뒤에 깔려 고를 수 없어, 팝오버 없는 버튼으로 둔다 */}
              <Box className="flex gap-4px">
                {EXPERIMENT_VARIANT_OPTIONS.map((option) => (
                  <Button
                    key={option.variant}
                    variant={
                      overriddenVariant === option.variant
                        ? 'contained'
                        : 'outlined'
                    }
                    size="small"
                    fullWidth
                    onClick={() =>
                      handleSelectVariant(overriddenName, option.variant)
                    }
                  >
                    {option.label}
                  </Button>
                ))}
              </Box>
            </li>
          ))}
        </ul>
      )}

      <SpaceY height={8} />
      <Button
        variant="contained"
        size="small"
        fullWidth
        disabled={!hasChanges}
        onClick={handleApply}
      >
        {hasChanges ? '변경' : '변경할 내용 없음'}
      </Button>
    </Box>
  );
};

export default StatsigExperimentOverrideSection;
